'use client';

import { Loader } from '@/components/ui/loader';
import { ModelViewer } from '@/components/ui/model-viewer';
import { VideoViewer } from '@/components/ui/video-viewer';
import { getStaticUiCopy } from '@/i18n/static-ui-copy';
import { cn } from '@/lib/utils';
import Image from 'next/image';
import { useLocale } from 'next-intl';
import {
  startTransition,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

interface FullscreenViewerMediaProps {
  src: string;
  alt: string;
  type: 'image' | 'video' | '3d';
}

export function FullscreenViewerMedia({
  src,
  alt,
  type,
}: FullscreenViewerMediaProps) {
  if (type === 'video') {
    return (
      <div
        className="relative flex max-h-[85vh] max-w-[95vw] items-center justify-center"
        onClick={(event) => event.stopPropagation()}
      >
        <VideoViewer
          key={src}
          src={src}
          className="aspect-auto h-auto max-h-[85vh] w-auto max-w-[95vw]"
          videoClassName="max-h-[85vh] max-w-[95vw] object-contain"
          controls={true}
          autoPlay={true}
          loop={true}
          muted={true}
        />
      </div>
    );
  }

  if (type === '3d') {
    return (
      <div
        className="relative flex items-center justify-center"
        style={{ width: '100dvw', height: '100dvh' }}
        onClick={(event) => event.stopPropagation()}
      >
        <ModelViewer
          key={src}
          src={src}
          alt={alt}
          className="h-full w-full"
          autoRotate={true}
          cameraControls={true}
          ar={true}
          style={{
            width: '100%',
            height: '100%',
            backgroundColor: 'transparent',
          }}
        />
      </div>
    );
  }

  return <ZoomableImage key={src} src={src} alt={alt} />;
}

interface ZoomableImageProps {
  src: string;
  alt: string;
}

function ZoomableImage({ src, alt }: ZoomableImageProps) {
  const locale = useLocale();
  const ui = getStaticUiCopy(locale);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const pointersRef = useRef<Map<number, { x: number; y: number }>>(new Map());
  const pinchStateRef = useRef<{
    initialDistance: number;
    initialScale: number;
  } | null>(null);

  const MIN_SCALE = 1;
  const MAX_SCALE = 8;
  const [scale, setScale] = useState<number>(1);
  const [translateX, setTranslateX] = useState<number>(0);
  const [translateY, setTranslateY] = useState<number>(0);
  const [isPanning, setIsPanning] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [fullImageLoaded, setFullImageLoaded] = useState<boolean>(false);
  const lastPointRef = useRef<{ x: number; y: number } | null>(null);

  const clamp = useCallback((value: number, min: number, max: number) => {
    return Math.min(Math.max(value, min), max);
  }, []);

  const getPanBounds = useCallback(() => {
    const container = containerRef.current;
    const image = imageRef.current;
    if (!container || !image) return { maxX: Infinity, maxY: Infinity };

    const rect = container.getBoundingClientRect();
    const containerWidth = rect.width;
    const containerHeight = rect.height;
    const naturalWidth = image.naturalWidth || containerWidth;
    const naturalHeight = image.naturalHeight || containerHeight;
    const containerRatio = containerWidth / containerHeight;
    const imageRatio = naturalWidth / naturalHeight;

    let baseWidth: number;
    let baseHeight: number;
    if (imageRatio > containerRatio) {
      baseWidth = containerWidth;
      baseHeight = containerWidth / imageRatio;
    } else {
      baseHeight = containerHeight;
      baseWidth = containerHeight * imageRatio;
    }

    const displayWidth = baseWidth * scale;
    const displayHeight = baseHeight * scale;
    return {
      maxX: Math.max(0, (displayWidth - containerWidth) / 2),
      maxY: Math.max(0, (displayHeight - containerHeight) / 2),
    };
  }, [scale]);

  const applyPanConstraints = useCallback(
    (x: number, y: number) => {
      const { maxX, maxY } = getPanBounds();
      return {
        x: clamp(x, -maxX, maxX),
        y: clamp(y, -maxY, maxY),
      };
    },
    [clamp, getPanBounds]
  );

  const zoomAt = useCallback(
    (deltaScale: number, clientX: number, clientY: number) => {
      const container = containerRef.current;
      if (!container) return;

      const rect = container.getBoundingClientRect();
      const offsetX = clientX - rect.left - rect.width / 2 - translateX;
      const offsetY = clientY - rect.top - rect.height / 2 - translateY;
      const newScale = clamp(scale * deltaScale, MIN_SCALE, MAX_SCALE);
      const scaleRatio = newScale / scale;
      const constrained = applyPanConstraints(
        translateX - offsetX * (scaleRatio - 1),
        translateY - offsetY * (scaleRatio - 1)
      );

      setScale(newScale);
      setTranslateX(constrained.x);
      setTranslateY(constrained.y);
    },
    [applyPanConstraints, clamp, scale, translateX, translateY]
  );

  const handleWheel = useCallback(
    (event: React.WheelEvent<HTMLDivElement>) => {
      if (event.cancelable) event.preventDefault();
      const factor = Math.exp(-event.deltaY * 0.0025);
      zoomAt(factor, event.clientX, event.clientY);
    },
    [zoomAt]
  );

  const handlePointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (
        event.pointerType === 'mouse' &&
        (event.button !== 0 || event.ctrlKey || event.metaKey)
      ) {
        return;
      }
      event.currentTarget.setPointerCapture?.(event.pointerId);
      pointersRef.current.set(event.pointerId, {
        x: event.clientX,
        y: event.clientY,
      });

      if (pointersRef.current.size === 2) {
        const points = Array.from(pointersRef.current.values());
        const first = points[0];
        const second = points[1];
        if (!first || !second) return;
        pinchStateRef.current = {
          initialDistance: Math.max(
            1,
            Math.hypot(first.x - second.x, first.y - second.y)
          ),
          initialScale: scale,
        };
        setIsPanning(false);
        lastPointRef.current = null;
      } else {
        setIsPanning(true);
        lastPointRef.current = { x: event.clientX, y: event.clientY };
      }
    },
    [scale]
  );

  const handlePointerMove = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (pointersRef.current.has(event.pointerId)) {
        pointersRef.current.set(event.pointerId, {
          x: event.clientX,
          y: event.clientY,
        });
      }

      if (pointersRef.current.size === 2 && pinchStateRef.current) {
        const points = Array.from(pointersRef.current.values());
        const first = points[0];
        const second = points[1];
        if (!first || !second) return;
        const distance = Math.max(
          1,
          Math.hypot(first.x - second.x, first.y - second.y)
        );
        const clampedScale = clamp(
          (pinchStateRef.current.initialScale * distance) /
            pinchStateRef.current.initialDistance,
          MIN_SCALE,
          MAX_SCALE
        );
        const factor = clampedScale / scale;
        if (!Number.isFinite(factor) || factor === 1) return;
        zoomAt(factor, (first.x + second.x) / 2, (first.y + second.y) / 2);
        return;
      }

      if (!isPanning || !lastPointRef.current) return;
      const nextX = translateX + event.clientX - lastPointRef.current.x;
      const nextY = translateY + event.clientY - lastPointRef.current.y;
      lastPointRef.current = { x: event.clientX, y: event.clientY };
      const constrained = applyPanConstraints(nextX, nextY);
      setTranslateX(constrained.x);
      setTranslateY(constrained.y);
    },
    [
      applyPanConstraints,
      clamp,
      isPanning,
      scale,
      translateX,
      translateY,
      zoomAt,
    ]
  );

  const handlePointerUp = useCallback(
    (event?: React.PointerEvent<HTMLDivElement>) => {
      if (event) pointersRef.current.delete(event.pointerId);
      if (pointersRef.current.size < 2) pinchStateRef.current = null;
      if (pointersRef.current.size === 0) {
        setIsPanning(false);
        lastPointRef.current = null;
      }
    },
    []
  );

  const handleDoubleClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const nextScale = scale < 2 ? 2 : 1;
      zoomAt(nextScale / scale, event.clientX, event.clientY);
    },
    [scale, zoomAt]
  );

  const resetView = useCallback(() => {
    setScale(1);
    setTranslateX(0);
    setTranslateY(0);
  }, []);
  const previousSrcRef = useRef('');

  useEffect(() => {
    if (previousSrcRef.current === src) return undefined;
    const frame = requestAnimationFrame(() => {
      resetView();
      setIsLoading(true);
      setFullImageLoaded(false);
      previousSrcRef.current = src;
    });
    return () => cancelAnimationFrame(frame);
  }, [resetView, src]);

  const handleFullImageLoad = useCallback(() => {
    setFullImageLoaded(true);
    setIsLoading(false);
  }, []);

  const handleFullImageError = useCallback(() => {
    setIsLoading(false);
    setFullImageLoaded(true);
  }, []);

  useEffect(() => {
    const image = imageRef.current;
    if (image?.complete && image.naturalWidth > 0) {
      startTransition(handleFullImageLoad);
    }
  }, [handleFullImageLoad, src]);

  return (
    <div
      ref={containerRef}
      className={cn(
        'relative flex h-full w-full touch-none items-center justify-center overflow-hidden select-none',
        isPanning ? 'cursor-grabbing' : 'cursor-grab'
      )}
      onWheel={handleWheel}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      onDoubleClick={handleDoubleClick}
      onClick={(event) => event.stopPropagation()}
      role="img"
      aria-label={alt}
    >
      {isLoading && !fullImageLoaded ? (
        <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center">
          <Image
            src={src}
            alt={alt}
            width={1200}
            height={1200}
            className="h-auto max-h-[90vh] w-auto max-w-[90vw] object-contain"
            unoptimized
            // Not `priority`: that hoists a <link rel="preload"> holding this
            // (signed, private) URL into <head>, which React never removes, so
            // the bearer URL outlived the viewer and the workspace (#4628).
            loading="eager"
            fetchPriority="high"
          />
        </div>
      ) : null}

      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        key={src}
        ref={imageRef}
        src={src}
        alt={alt}
        draggable={false}
        onLoad={handleFullImageLoad}
        onError={handleFullImageError}
        className={cn(
          'max-h-[90vh] max-w-[90vw] object-contain transition-opacity duration-200',
          fullImageLoaded
            ? 'visible opacity-100'
            : 'pointer-events-none invisible opacity-0'
        )}
        style={{
          transform: `translate3d(${translateX}px, ${translateY}px, 0) scale(${scale})`,
          transition: isPanning ? 'none' : 'transform 60ms ease-out',
          willChange: 'transform',
        }}
      />

      {isLoading ? (
        <div className="fixed top-4 left-1/2 z-[10002] flex -translate-x-1/2 items-center gap-1.5 rounded-lg border border-white/20 bg-black/70 px-2.5 py-1.5 text-xs text-white backdrop-blur-sm">
          <Loader size="sm" />
          <span>{ui.loadingFullImage}</span>
        </div>
      ) : null}
    </div>
  );
}
